/**
 * Picking workspace - touch + scanner + numpad.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Loader2, ScanLine, Square } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { HubBreadcrumbTitleRow } from "@/components/HubPageTitle";
import { Button } from "@/components/ui/Button";
import { playPairFailureSound, playPairSuccessSound } from "@/lib/floorStationPairAudio";
import { applyHidScanKey, useFloorPageScanCapture } from "@/lib/floorPageScanCapture";
import { useNavigationTrail, usePageBreadcrumb } from "@/lib/navigationTrailContext";
import { ApiError } from "@/lib/api";
import { fetchInventoryBox, type InventoryBox } from "@/lib/inventoryBoxApi";
import { useHubToast } from "@/components/ui/HubToast";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { isSgsLauncherAvailable } from "@/lib/sgsLauncher";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import {
  kioskMainPaneOrderClass,
  kioskNumpadPaneOrderClass,
  kittingPickNumpadOnBottom,
  kittingPickNumpadOnRight,
  kittingPickNumpadSplitGridClass,
  KITTING_PICK_NUMPAD_CORNER_STORAGE_KEY,
  PREPACK_TRANSFER_KIOSK_HEIGHT_CLASS,
  readStoredKittingPickNumpadCorner,
  TransferPackInputMode,
  TransferPackNumpad,
  TransferPackQuantityReadout,
} from "@/pages/prepack/prepackTransferTouchUi";

import { kittingSubmitPick, kittingReturnPick } from "./kittingScanApi";
import { transferFloorRequestToast } from "./transferFloorRequestToast";
import {
  kittingInventoryBoxScanError,
  kittingPickLineForCartScan,
  kittingPickLinesMatchingSku,
  kittingPickLinesMatchingSkuAtLocation,
  kittingPickerCandidatesWithSelectedLine,
  kittingPickerDisplayLines,
  kittingReconcileOpenPickSelection,
  kittingUniqueSkuLocationGroups,
  kittingBufferedTextIsScan,
  kittingMaterialScanTarget,
  kittingPickContainersFromHexes,
  parseKittingQuantityDraft,
  planKittingQuantityModeKey,
} from "./kittingScanModel";
import {
  KITTING_PICK_SLOTS_QUERY_KEY,
  parseKittingPickSlotIds,
} from "./kittingOrderSelectionModel";
import {
  KITTING_PICK_MARKS_QUERY_KEY,
  kittingCartLetterToSlotKeyMap,
  kittingCartMarksFromRunRecord,
  resolveKittingCartMarks,
  resolveKittingCartScanToSlotKey,
  type KittingCartMark,
} from "./kittingCartMarks";
import { KittingInventoryDiscrepancyButton } from "./KittingInventoryDiscrepancyButton";
import { KittingInventoryBoxPickModal } from "./KittingInventoryBoxPickModal";
import { KittingInventoryLocationChangeButton } from "./KittingInventoryLocationChangeButton";
import { InboundZplPrinterHeaderButton } from "@/pages/shipping/InboundZplPrinterHeaderButton";
import { useInboundZplPrinter } from "@/pages/shipping/inboundZplPrinter";
import { WarehouseRecountModal } from "@/pages/warehouse/WarehouseRecountModal";
import {
  KittingPickCartPanel,
  KittingPickHScrollStrip,
  KittingPickLinePickerRow,
  KittingPickNumpadCornerToggle,
  KittingPickSkuTile,
} from "./kittingPickTouchUi";
import type { KittingCartSlot } from "./kittingCartModel";
import type { KittingPickLine } from "./kittingPickModel";
import {
  kittingPickLineForCartEntry,
  kittingPickUnlabeledQuantity,
  type KittingPickCartEntry,
} from "./kittingPickCartModel";
import { useKittingPickPageData } from "./useKittingPickPageData";
import { useKittingPickLabor } from "./useKittingPickLabor";
import {
  fetchKittingPickRun,
  finishKittingPickRun,
  kittingCurrentPickRunQueryKey,
  kittingPickRunQueryKey,
  KITTING_PICK_RUN_QUERY_KEY,
  parseKittingPickRunId,
  type KittingPickRun,
} from "./kittingPickRunApi";
import {
  applyKittingPickActionResponse,
  applyOptimisticKittingPickAction,
  applyOptimisticKittingReturnAction,
  rollbackKittingPickActionCache,
  snapshotKittingPickActionCache,
  type KittingPickActionCacheSnapshot,
} from "./kittingPickActionCache";
import {
  kittingPickInventorySelection,
  kittingPickKnownStorageLocations,
} from "./kittingPickInventorySelection";

const KITTING_PICK_PAGE_HELP =
  "Scan a component SKU or tap a line tile, choose the job when several lines match, enter quantity on the keypad or USB numpad, then press Enter to allocate to the work order or transfer carton.";

function focusScanInput(input: HTMLInputElement | null): void {
  if (input == null) return;
  requestAnimationFrame(() => {
    input.focus({ preventScroll: true });
  });
}

export function KittingPickPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const canWrite = useHubModuleWrite("manufacturing");
  const scanInputRef = useRef<HTMLInputElement>(null);
  const submitQuantityLockRef = useRef(false);
  const scannedContainerQuantityRef = useRef<number | null>(null);
  const selectedInventoryBoxHexesRef = useRef<string[]>([]);
  const unpickedInventoryBoxHexesRef = useRef<string[]>([]);
  const [numpadLocked, setNumpadLocked] = useState(false);
  const [scannedInventoryBox, setScannedInventoryBox] = useState<InventoryBox | null>(null);
  const [awaitingKitForContainer, setAwaitingKitForContainer] = useState(false);
  const quantityScanBufferRef = useRef("");
  const handleScanRef = useRef<(scanned: string) => void>(() => {});
  const submitQuantityRef = useRef<() => void>(() => {});
  const numpadLockedRef = useRef(false);
  const inputModeRef = useRef<TransferPackInputMode>("scan");
  const awaitingKitForContainerRef = useRef(false);
  const commitContainerPickRef = useRef<(line: KittingPickLine, quantity: number) => void>(() => {});
  const [recountOpen, setRecountOpen] = useState(false);
  const [finishConfirmRun, setFinishConfirmRun] = useState<KittingPickRun | null>(null);
  const { station, setStation, selectedPrinter, unavailableMessage, documents } = useInboundZplPrinter();

  const runId = useMemo(
    () => parseKittingPickRunId(searchParams.get(KITTING_PICK_RUN_QUERY_KEY)),
    [searchParams],
  );

  const runQuery = useQuery({
    queryKey: kittingPickRunQueryKey(runId),
    queryFn: () => fetchKittingPickRun(runId!),
    enabled: runId != null,
    refetchOnWindowFocus: false,
  });

  const finishRunMutation = useMutation({
    mutationFn: async (input: { runId: number; confirmCartNotEmpty: boolean }) =>
      finishKittingPickRun(input.runId, { confirmCartNotEmpty: input.confirmCartNotEmpty }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: kittingCurrentPickRunQueryKey() });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "kitting", "pick-data"] });
      setFinishConfirmRun(null);
      showToast({ message: "Pick run finished.", tone: "success" });
      navigate("/manufacturing/kitting");
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        const body = error.body as { warning?: string; run?: KittingPickRun } | undefined;
        if (body?.warning === "cart_has_quantities" && body.run != null) {
          setFinishConfirmRun(body.run);
          return;
        }
      }
      const message = error instanceof ApiError ? error.message : "Could not finish pick run.";
      showToast({ message, tone: "error" });
    },
  });

  const legacySlotIds = useMemo(
    () => parseKittingPickSlotIds(searchParams.get(KITTING_PICK_SLOTS_QUERY_KEY)),
    [searchParams],
  );

  const slotIds = useMemo(() => {
    if (runQuery.data != null) return runQuery.data.slotKeys;
    return legacySlotIds;
  }, [runQuery.data, legacySlotIds]);

  const cartMarks = useMemo(() => {
    if (runQuery.data != null) {
      return kittingCartMarksFromRunRecord(runQuery.data.slotKeys, runQuery.data.cartMarks);
    }
    return resolveKittingCartMarks(slotIds, searchParams.get(KITTING_PICK_MARKS_QUERY_KEY));
  }, [runQuery.data, searchParams, slotIds]);

  const cartLetterToSlotKey = useMemo(
    () => kittingCartLetterToSlotKeyMap(cartMarks),
    [cartMarks],
  );

  const {
    cartSlotById,
    scopedPickLines,
    cartEntries,
    loading,
    error,
    hoverHints,
  } = useKittingPickPageData(slotIds, runQuery.data ?? null);

  const [inputMode, setInputMode] = useState<TransferPackInputMode>("scan");
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [selectedStorageLocation, setSelectedStorageLocation] = useState<string | null>(null);
  const [candidateLines, setCandidateLines] = useState<KittingPickLine[]>([]);
  const [selectedLine, setSelectedLine] = useState<KittingPickLine | null>(null);
  const [quantityDraft, setQuantityDraft] = useState("");
  const [numpadCorner, setNumpadCorner] = useState(readStoredKittingPickNumpadCorner);
  const [returningCartEntryId, setReturningCartEntryId] = useState<string | null>(null);

  const skuGroups = useMemo(() => kittingUniqueSkuLocationGroups(scopedPickLines), [scopedPickLines]);
  const pickerDisplayLines = useMemo(
    () => kittingPickerDisplayLines(candidateLines, selectedLine),
    [candidateLines, selectedLine],
  );

  const selectedSlots = useMemo(
    () => slotIds.map((id) => cartSlotById.get(id)).filter((slot) => slot != null),
    [slotIds, cartSlotById],
  );

  const { laborGateReady, touchActivity: touchLaborActivity } = useKittingPickLabor({
    slotIds,
    runId,
    selectedSlots,
    scopedPickLines,
    canWrite,
  });

  const pageTitle = useMemo(() => {
    if (selectedSlots.length === 0) return "Picking";
    if (selectedSlots.length === 1) return selectedSlots[0]!.headlineLabel;
    return `${String(selectedSlots.length)} jobs`;
  }, [selectedSlots]);

  usePageBreadcrumb(pageTitle);
  const { trail } = useNavigationTrail();

  const inventorySelection = useMemo(
    () =>
      kittingPickInventorySelection({
        selectedLine,
        selectedSku,
        selectedStorageLocation,
        candidateLines,
      }),
    [candidateLines, selectedLine, selectedSku, selectedStorageLocation],
  );

  const knownStorageLocations = useMemo(
    () => kittingPickKnownStorageLocations(scopedPickLines),
    [scopedPickLines],
  );

  const clearScannedContainer = useCallback(() => {
    selectedInventoryBoxHexesRef.current = [];
    unpickedInventoryBoxHexesRef.current = [];
    quantityScanBufferRef.current = "";
    scannedContainerQuantityRef.current = null;
    awaitingKitForContainerRef.current = false;
    setAwaitingKitForContainer(false);
  }, []);

  const returnToScanMode = useCallback(() => {
    clearScannedContainer();
    setInputMode("scan");
    setCandidateLines([]);
    setSelectedLine(null);
    setSelectedSku(null);
    setSelectedStorageLocation(null);
    setQuantityDraft("");
    focusScanInput(scanInputRef.current);
  }, [clearScannedContainer]);

  const selectLine = useCallback((line: KittingPickLine) => {
    if (line.qtyDue <= 0) return;
    if (selectedLine?.id === line.id) {
      clearScannedContainer();
      setSelectedLine(null);
      setQuantityDraft("");
      setInputMode("scan");
      focusScanInput(scanInputRef.current);
      return;
    }
    const pendingQuantity = scannedContainerQuantityRef.current;
    const commitPending = awaitingKitForContainerRef.current && pendingQuantity != null;
    if (!commitPending) {
      clearScannedContainer();
    }
    setCandidateLines((current) => kittingPickerCandidatesWithSelectedLine(current, line));
    setSelectedLine(line);
    setSelectedSku(line.sku);
    setSelectedStorageLocation(line.storageLocation);
    if (commitPending) {
      scannedContainerQuantityRef.current = null;
      awaitingKitForContainerRef.current = false;
      setAwaitingKitForContainer(false);
      setQuantityDraft("");
      setInputMode("quantity");
      commitContainerPickRef.current(line, pendingQuantity);
      return;
    }
    const scannedQuantity = scannedContainerQuantityRef.current;
    scannedContainerQuantityRef.current = null;
    setQuantityDraft(scannedQuantity == null ? "" : String(scannedQuantity));
    setInputMode("quantity");
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [clearScannedContainer, selectedLine]);

  const selectSkuAtLocation = useCallback(
    (sku: string, storageLocation: string) => {
      const isCurrentlySelected =
        (selectedLine != null
          && selectedLine.sku === sku
          && selectedLine.storageLocation === storageLocation
          && inputMode === "quantity")
        || (selectedSku === sku
          && selectedStorageLocation === storageLocation
          && candidateLines.length > 0);

      if (isCurrentlySelected) {
        returnToScanMode();
        return;
      }

      const matches = kittingPickLinesMatchingSkuAtLocation(scopedPickLines, sku, storageLocation);
      if (matches.length === 0) {
        showToast({
          message: `No open pick line for SKU ${sku.trim()} at ${storageLocation}.`,
          tone: "error",
        });
        focusScanInput(scanInputRef.current);
        return;
      }
      if (matches.length === 1) {
        setCandidateLines([]);
        selectLine(matches[0]!);
        return;
      }
      setSelectedSku(sku);
      setSelectedStorageLocation(storageLocation);
      setCandidateLines(matches);
      setSelectedLine(null);
      setInputMode("scan");
    },
    [
      candidateLines.length,
      inputMode,
      returnToScanMode,
      scopedPickLines,
      selectLine,
      selectedLine,
      selectedSku,
      selectedStorageLocation,
      showToast,
    ],
  );

  const selectSku = useCallback(
    (sku: string, preserveInventoryBoxes = false, requireKitChoice = false) => {
      if (!preserveInventoryBoxes) selectedInventoryBoxHexesRef.current = [];
      const matches = kittingPickLinesMatchingSku(scopedPickLines, sku);
      if (matches.length === 0) {
        if (requireKitChoice) clearScannedContainer();
        showToast({ message: `No open pick line for SKU ${sku.trim()}.`, tone: "error" });
        focusScanInput(scanInputRef.current);
        return;
      }
      if (requireKitChoice) {
        setSelectedSku(sku);
        setSelectedStorageLocation(matches.length === 1 ? matches[0]!.storageLocation : null);
        setCandidateLines(matches);
        setSelectedLine(null);
        setInputMode("scan");
        awaitingKitForContainerRef.current = true;
        setAwaitingKitForContainer(true);
        showToast({ message: `Select the kit for ${sku.trim()}.`, tone: "info" });
        focusScanInput(scanInputRef.current);
        return;
      }
      if (matches.length === 1) {
        setCandidateLines([]);
        selectLine(matches[0]!);
        return;
      }
      const locations = [...new Set(matches.map((line) => line.storageLocation))];
      if (locations.length === 1) {
        selectSkuAtLocation(sku, locations[0]!);
        return;
      }
      setSelectedSku(sku);
      setSelectedStorageLocation(null);
      setCandidateLines(matches);
      setSelectedLine(null);
      setInputMode("scan");
    },
    [clearScannedContainer, scopedPickLines, selectLine, selectSkuAtLocation, showToast],
  );

  const handleScan = useCallback(
    (scanned: string) => {
      const cartMatch = kittingPickLineForCartScan(scanned, {
        activeSlotIds: slotIds,
        letterToSlotKey: cartLetterToSlotKey,
        candidateLines,
        scopedPickLines,
        selectedSku,
        selectedStorageLocation,
      });

      if (cartMatch != null) {
        selectLine(cartMatch.line);
        return;
      }

      const cartSlotOnly = resolveKittingCartScanToSlotKey(
        scanned,
        slotIds,
        cartLetterToSlotKey,
      );
      if (cartSlotOnly != null) {
        const slot = cartSlotById.get(cartSlotOnly.slotKey);
        const slotLabel = slot?.headlineLabel ?? cartSlotOnly.slotKey;
        if (selectedSku == null) {
          showToast({
            message: `Cart bin ${slotLabel} scanned. Tap or scan a component SKU, then scan the bin again to pick for that job.`,
            tone: "info",
          });
        } else {
          showToast({
            message: `No open line for ${selectedSku.trim()} on cart bin ${slotLabel}.`,
            tone: "error",
          });
        }
        focusScanInput(scanInputRef.current);
        return;
      }

      const material = kittingMaterialScanTarget(scanned);
      if (material == null) {
        showToast({ message: "Could not read that scan as a SKU or inventory box.", tone: "error" });
        focusScanInput(scanInputRef.current);
        return;
      }
      if (material.kind === "sku") {
        selectSku(material.sku);
        return;
      }
      void fetchInventoryBox(material.hex)
        .then((box) => {
          const scanError = kittingInventoryBoxScanError({
            sku: box.sku,
            shortCode: box.short_code,
            archived: box.archived_at != null,
            matchingOpenLineCount: kittingPickLinesMatchingSku(scopedPickLines, box.sku).length,
            midPick: box.kitting_pick == null
              ? null
              : { runId: box.kitting_pick.run_id, slotKey: box.kitting_pick.slot_key },
          });
          if (scanError != null) {
            throw new Error(scanError);
          }
          if (
            selectedLine != null
            && selectedLine.sku.trim().toLowerCase() !== box.sku.trim().toLowerCase()
          ) {
            throw new Error(`That container is ${box.sku}, not ${selectedLine.sku}.`);
          }
          setScannedInventoryBox(box);
        })
        .catch((error) => {
          const notFound = error instanceof ApiError && error.status === 404;
          showToast({
            message: notFound
              ? "That inventory box was not found."
              : error instanceof Error
                ? error.message
                : "That inventory box was not found.",
            tone: "error",
          });
          focusScanInput(scanInputRef.current);
        });
    },
    [
      candidateLines,
      cartLetterToSlotKey,
      cartSlotById,
      scopedPickLines,
      selectLine,
      selectSku,
      selectedLine,
      selectedSku,
      selectedStorageLocation,
      showToast,
      slotIds,
    ],
  );

  useFloorPageScanCapture({
    enabled: canWrite && scannedInventoryBox == null && !recountOpen,
    // The page listener owns wedge scans so a barcode can finish after quantity mode ends.
    captureUnfocusedHid: false,
    inputRef: scanInputRef,
    onScan: handleScan,
  });

  const releaseNumpadLock = useCallback(() => {
    submitQuantityLockRef.current = false;
    setNumpadLocked(false);
  }, []);

  const submitMutation = useMutation({
    mutationFn: async ({ line, quantity }: { line: KittingPickLine; quantity: number }) =>
      kittingSubmitPick(line, quantity, {
        kittingRunId: runId,
        inventoryBoxHexes: selectedInventoryBoxHexesRef.current,
        unpickedInventoryBoxHexes: unpickedInventoryBoxHexesRef.current,
      }),
    onMutate: async ({ line, quantity }) => {
      if (runId == null) {
        return {};
      }
      const snapshot = snapshotKittingPickActionCache(queryClient, slotIds, runId);
      applyOptimisticKittingPickAction(
        queryClient,
        slotIds,
        runId,
        line,
        quantity,
        kittingPickContainersFromHexes(selectedInventoryBoxHexesRef.current, quantity),
      );
      setQuantityDraft("");
      setInputMode("quantity");
      releaseNumpadLock();
      return { snapshot };
    },
    onSuccess: (response, variables) => {
      clearScannedContainer();
      if (response != null) {
        applyKittingPickActionResponse(queryClient, slotIds, runId, response);
      }
      playPairSuccessSound();
      showToast({
        message: transferFloorRequestToast(
          `Picked ${variables.quantity}× ${variables.line.sku} → ${variables.line.destinationLabel}.`,
          response ?? {},
        ),
        tone: "success",
      });
    },
    onError: (submitError: unknown, _variables, context) => {
      const snapshot = (context as { snapshot?: KittingPickActionCacheSnapshot } | undefined)?.snapshot;
      if (snapshot != null && runId != null) {
        rollbackKittingPickActionCache(queryClient, slotIds, runId, snapshot);
      }
      playPairFailureSound();
      const message = submitError instanceof ApiError ? submitError.message : "Could not record pick.";
      showToast({ message, tone: "error" });
    },
    onSettled: () => {
      releaseNumpadLock();
    },
  });

  commitContainerPickRef.current = (line, quantity) => {
    if (submitQuantityLockRef.current) return;
    submitQuantityLockRef.current = true;
    setNumpadLocked(true);
    submitMutation.mutate({ line, quantity });
  };

  const returnCartMutation = useMutation({
    mutationFn: async ({
      line,
      quantity,
      inventoryBoxHex,
      returnUnlabeledOnly,
    }: {
      entry: KittingPickCartEntry;
      line: KittingPickLine;
      quantity: number;
      inventoryBoxHex?: string;
      returnUnlabeledOnly?: boolean;
      returnKey: string;
    }) =>
      kittingReturnPick(line, quantity, { kittingRunId: runId, inventoryBoxHex, returnUnlabeledOnly }),
    onMutate: async ({ line, quantity, inventoryBoxHex, returnKey }) => {
      setReturningCartEntryId(returnKey);
      if (runId == null) {
        return {};
      }
      const snapshot = snapshotKittingPickActionCache(queryClient, slotIds, runId);
      applyOptimisticKittingReturnAction(queryClient, slotIds, runId, line, quantity, inventoryBoxHex);
      return { snapshot };
    },
    onSuccess: (response, variables) => {
      if (response != null) {
        applyKittingPickActionResponse(queryClient, slotIds, runId, response);
      }
      playPairSuccessSound();
      showToast({
        message: transferFloorRequestToast(
          `Returned ${variables.quantity}× ${variables.entry.sku} from ${variables.entry.destinationLabel}.`,
          response ?? {},
        ),
        tone: "success",
      });
    },
    onError: (returnError: unknown, _variables, context) => {
      const snapshot = (context as { snapshot?: KittingPickActionCacheSnapshot } | undefined)?.snapshot;
      if (snapshot != null && runId != null) {
        rollbackKittingPickActionCache(queryClient, slotIds, runId, snapshot);
      }
      playPairFailureSound();
      const message = returnError instanceof ApiError ? returnError.message : "Could not return pick.";
      showToast({ message, tone: "error" });
    },
    onSettled: () => {
      setReturningCartEntryId(null);
    },
  });

  const handleReturnCartEntry = useCallback(
    (entry: KittingPickCartEntry) => {
      if (!canWrite || returnCartMutation.isPending) return;
      const line = kittingPickLineForCartEntry(entry, scopedPickLines);
      const quantity = kittingPickUnlabeledQuantity(entry);
      if (quantity < 1) return;
      returnCartMutation.mutate({
        entry,
        line,
        quantity,
        returnUnlabeledOnly: true,
        returnKey: entry.id,
      });
    },
    [canWrite, returnCartMutation, scopedPickLines],
  );

  const handleReturnCartContainer = useCallback(
    (entry: KittingPickCartEntry, container: KittingPickCartEntry["pickedContainers"][number]) => {
      if (!canWrite || returnCartMutation.isPending) return;
      if (container.quantity < 1 || container.hex.trim() === "") return;
      const line = kittingPickLineForCartEntry(entry, scopedPickLines);
      returnCartMutation.mutate({
        entry,
        line,
        quantity: container.quantity,
        inventoryBoxHex: container.hex,
        returnKey: `${entry.id}::${container.hex}`,
      });
    },
    [canWrite, returnCartMutation, scopedPickLines],
  );

  const submitQuantity = useCallback(() => {
    if (submitQuantityLockRef.current || selectedLine == null || numpadLocked) return;
    const quantity = parseKittingQuantityDraft(quantityDraft);
    if (quantity == null) {
      playPairFailureSound();
      showToast({ message: "Enter a positive quantity.", tone: "error" });
      return;
    }
    submitQuantityLockRef.current = true;
    setNumpadLocked(true);
    submitMutation.mutate({ line: selectedLine, quantity });
  }, [numpadLocked, quantityDraft, selectedLine, showToast, submitMutation]);

  const appendDigit = useCallback((digit: string) => {
    setQuantityDraft((current) => {
      if (current === "0") return digit;
      if (current.length >= 6) return current;
      return `${current}${digit}`;
    });
  }, []);

  const clearQuantity = useCallback(() => {
    setQuantityDraft("");
  }, []);

  const backspaceQuantity = useCallback(() => {
    setQuantityDraft((current) => current.slice(0, -1));
  }, []);

  useEffect(() => {
    if (inputMode !== "scan" || !canWrite) return;
    if (kittingBufferedTextIsScan(quantityScanBufferRef.current)) return;
    focusScanInput(scanInputRef.current);
  }, [canWrite, inputMode, scopedPickLines.length]);

  useEffect(() => {
    const next = kittingReconcileOpenPickSelection({
      scopedPickLines,
      selectedLine,
      selectedSku,
      selectedStorageLocation,
      candidateLines,
    });
    if (next == null) return;

    setCandidateLines((current) => {
      const unchanged =
        current.length === next.candidateLines.length
        && current.every((line, index) => line === next.candidateLines[index]);
      return unchanged ? current : next.candidateLines;
    });
    setSelectedLine(next.selectedLine);
    setSelectedSku(next.selectedSku);
    setSelectedStorageLocation(next.selectedStorageLocation);
    setInputMode(next.inputMode);
    if (next.selectedSku == null && next.selectedLine == null) {
      setQuantityDraft("");
      if (!kittingBufferedTextIsScan(quantityScanBufferRef.current)) {
        focusScanInput(scanInputRef.current);
      }
    }
  }, [candidateLines, scopedPickLines, selectedLine, selectedSku, selectedStorageLocation]);

  useEffect(() => {
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(KITTING_PICK_NUMPAD_CORNER_STORAGE_KEY, numpadCorner);
    } catch {
      /* ignore */
    }
  }, [numpadCorner]);

  if (inputModeRef.current !== inputMode) {
    if (!kittingBufferedTextIsScan(quantityScanBufferRef.current)) {
      quantityScanBufferRef.current = "";
    }
    inputModeRef.current = inputMode;
  }
  handleScanRef.current = handleScan;
  submitQuantityRef.current = submitQuantity;
  numpadLockedRef.current = numpadLocked;

  const scanCaptureEnabled = canWrite && scannedInventoryBox == null && !recountOpen;

  // One wedge listener for the whole page. A barcode that started in quantity mode
  // must survive the line closing, or Enter submits only the carton-id tail.
  useEffect(() => {
    if (!scanCaptureEnabled) {
      quantityScanBufferRef.current = "";
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const inPackNumpad = target.closest("[data-transfer-pack-numpad]") != null;
      const blocksPackKeys =
        target !== scanInputRef.current
        && (
          target.closest("input, textarea, select") != null
          || (target.closest("button, a") != null && !inPackNumpad)
        );

      if (target === scanInputRef.current || blocksPackKeys) return;

      if (inputModeRef.current !== "quantity") {
        const next = applyHidScanKey(quantityScanBufferRef.current, event.key);
        if (next.buffer === quantityScanBufferRef.current && next.submitted == null) return;
        event.preventDefault();
        quantityScanBufferRef.current = next.buffer;
        if (next.submitted != null) {
          handleScanRef.current(next.submitted);
        }
        return;
      }

      const plan = planKittingQuantityModeKey(quantityScanBufferRef.current, event.key);
      quantityScanBufferRef.current = plan.buffer;

      if (plan.action === "submit-scan") {
        event.preventDefault();
        handleScanRef.current(plan.scanned);
        return;
      }
      if (plan.action === "accumulate-scan" || plan.action === "backspace-scan") {
        event.preventDefault();
        return;
      }
      if (plan.action === "ignore") return;

      if (event.key === "Enter") {
        if (!numpadLockedRef.current && !submitQuantityLockRef.current) {
          event.preventDefault();
          submitQuantityRef.current();
        }
        return;
      }

      if (plan.action === "backspace-quantity") {
        event.preventDefault();
        setQuantityDraft((current) => current.slice(0, -1));
        return;
      }
      if (plan.action === "digit") {
        event.preventDefault();
        appendDigit(plan.digit);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [appendDigit, scanCaptureEnabled]);

  if (slotIds.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-rose-700">
        No jobs selected.{" "}
        <Link className="font-medium text-sky-700 hover:text-sky-900" to="/manufacturing/kitting">
          Return to picking
        </Link>
        .
      </p>
    );
  }

  if (loading || (runId != null && runQuery.isLoading)) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading pick lines…
      </div>
    );
  }

  if (error != null || runQuery.error != null) {
    const message = error?.message ?? runQuery.error?.message ?? "Failed to load pick run.";
    return <p className="py-8 text-center text-sm text-rose-700">{message}</p>;
  }

  const invalidSlots = slotIds.filter((id) => !cartSlotById.has(id));
  if (invalidSlots.length > 0 || selectedSlots.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-rose-700">
        One or more selected jobs are no longer active.{" "}
        <Link className="font-medium text-sky-700 hover:text-sky-900" to="/manufacturing/kitting">
          Return to picking
        </Link>
        .
      </p>
    );
  }

  const workspaceDisabled =
    !canWrite || !laborGateReady || scopedPickLines.length === 0;
  const numpadOnRight = kittingPickNumpadOnRight(numpadCorner);
  const numpadOnBottom = kittingPickNumpadOnBottom(numpadCorner);
  const tabletWebView = isSgsLauncherAvailable();

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col overflow-hidden pb-12",
        tabletWebView ? "h-dvh" : cn("-my-6", PREPACK_TRANSFER_KIOSK_HEIGHT_CLASS),
      )}
      onPointerDown={touchLaborActivity}
      onKeyDown={touchLaborActivity}
    >
      <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-slate-50 px-1 py-1.5 xl:flex-wrap xl:py-2">
        <div className="min-w-0 flex-1">
          {trail.length > 0 ? (
            <HubBreadcrumbTitleRow
              className="min-w-0"
              title={
                <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="truncate text-base font-semibold text-slate-900 xl:text-lg">{pageTitle}</span>
                </span>
              }
              helpGlyph={<HoverHintWithGlyph hoverHint={KITTING_PICK_PAGE_HELP} />}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <h1 className="truncate text-base font-semibold text-slate-900 xl:text-lg">{pageTitle}</h1>
              <HoverHintWithGlyph hoverHint={KITTING_PICK_PAGE_HELP} />
            </div>
          )}
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
            {selectedSlots.map((slot) => (
              <KittingPickSlotChip
                key={slot.slotKey}
                slot={slot}
                mark={cartMarks.get(slot.slotKey) ?? null}
              />
            ))}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {runQuery.data?.status === "open" ? (
            <Button
              type="button"
              variant="primary"
              className="min-h-12 xl:h-8"
              disabled={!canWrite || finishRunMutation.isPending}
              onClick={() => {
                const run = runQuery.data;
                if (run == null || finishRunMutation.isPending) return;
                finishRunMutation.mutate({ runId: run.id, confirmCartNotEmpty: false });
              }}
            >
              <Square className="mr-1.5 h-4 w-4" aria-hidden />
              Finish run
            </Button>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            className="min-h-12 xl:h-8"
            onClick={() => setRecountOpen(true)}
          >
            <ClipboardList className="mr-1.5 h-4 w-4" aria-hidden />
            Recount / label
          </Button>
          <InboundZplPrinterHeaderButton
            station={station}
            onStationChange={setStation}
            unavailableMessage={unavailableMessage}
            className="min-h-12 xl:h-8"
          
      documents={documents}
    />
          <KittingInventoryDiscrepancyButton
            initialSku={inventorySelection?.sku ?? ""}
            transferOrderId={inventorySelection?.transferOrderId ?? null}
            transferOrderItemId={inventorySelection?.transferOrderItemId ?? null}
            disabled={inventorySelection == null}
            className="min-h-12 xl:h-8"
          />
          <KittingInventoryLocationChangeButton
            selection={inventorySelection}
            knownStorageLocations={knownStorageLocations}
            disabled={inventorySelection == null}
            className="min-h-12 xl:h-8"
          />
        </div>
      </header>

      {finishConfirmRun != null ? (
        <div
          className="shrink-0 border-b border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-950"
          role="alertdialog"
          aria-labelledby="kitting-pick-finish-confirm-heading"
        >
          <h2 id="kitting-pick-finish-confirm-heading" className="font-semibold">
            Finish pick run with items still in cart?
          </h2>
          <p className="mt-2 leading-snug">
            This run still has picked quantities in the cart. Work order allocation is already recorded.
            Finishing closes the run so the cart can be delivered to the station.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="primary"
              type="button"
              className="min-h-12 xl:h-8"
              disabled={finishRunMutation.isPending}
              onClick={() => {
                if (finishRunMutation.isPending) return;
                finishRunMutation.mutate({ runId: finishConfirmRun.id, confirmCartNotEmpty: true });
              }}
            >
              Finish anyway
            </Button>
            <Button
              size="sm"
              variant="secondary"
              type="button"
              className="min-h-12 xl:h-8"
              disabled={finishRunMutation.isPending}
              onClick={() => setFinishConfirmRun(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <section
        aria-label="Pick lines"
        className="shrink-0 border-b border-slate-200 bg-white px-1 py-1"
      >
        {scopedPickLines.length === 0 ? (
          <p className="px-2 py-3 text-sm text-slate-600">No open pick lines for the selected jobs.</p>
        ) : (
          <KittingPickHScrollStrip ariaLabel="Component SKUs">
            <div
              className={cn(
                "flex w-max min-w-full items-stretch gap-1.5",
                numpadOnRight ? "xl:justify-end" : "xl:justify-start",
              )}
            >
              {skuGroups.map((group) => (
                <KittingPickSkuTile
                  key={`${group.sku}::${group.storageLocation}`}
                  sku={group.sku}
                  storageLocation={group.storageLocation}
                  hint={hoverHints?.[group.sku.trim()]}
                  selected={
                    (selectedLine != null
                      && selectedLine.sku === group.sku
                      && selectedLine.storageLocation === group.storageLocation
                      && inputMode === "quantity")
                    || (selectedSku === group.sku
                      && selectedStorageLocation === group.storageLocation
                      && candidateLines.length > 0)
                  }
                  disabled={workspaceDisabled}
                  onSelect={() => selectSkuAtLocation(group.sku, group.storageLocation)}
                />
              ))}
            </div>
          </KittingPickHScrollStrip>
        )}
      </section>

      <section
        aria-label="Scan and line selection"
        className="flex min-w-0 shrink-0 flex-col gap-1.5 overflow-hidden border-b border-slate-200 bg-white px-1 py-1.5"
      >
        {!canWrite ? (
          <p className="text-sm text-slate-600">Manufacturing write access is required to record picks.</p>
        ) : (
          <>
            <label className="block">
              <span className="sr-only">Component SKU</span>
              <div className="relative">
                <ScanLine
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-500"
                  aria-hidden
                />
                <input
                  ref={scanInputRef}
                  type="text"
                  autoComplete="off"
                  placeholder={
                    awaitingKitForContainer
                      ? "Select the kit for this container"
                      : inputMode === "quantity" && selectedLine != null
                        ? "Scan the container for this bin"
                        : candidateLines.length > 0 && selectedLine == null
                          ? "Tap or scan the job line this pick is for"
                          : "Scan or tap a component SKU"
                  }
                  disabled={workspaceDisabled}
                  className={cn(
                    "h-12 w-full rounded-md border-2 border-brand-300 bg-white pl-10 pr-3 font-mono text-base text-slate-900 xl:rounded-lg xl:pl-11 xl:pr-4 xl:text-lg",
                    "placeholder:text-slate-400",
                    "focus:outline-none focus:ring-0",
                    "disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500",
                  )}
                  data-sgs-scan-target=""
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    const scanned = event.currentTarget.value;
                    event.currentTarget.value = "";
                    if (scanned.trim() === "") return;
                    handleScan(scanned);
                  }}
                />
              </div>
            </label>

            <KittingPickLinePickerRow
              candidateLines={pickerDisplayLines}
              marksBySlotId={cartMarks}
              selectedLineId={selectedLine?.id ?? null}
              onSelectLine={selectLine}
              placeholder={awaitingKitForContainer ? "Select the kit for this container" : undefined}
              alignEnd={numpadOnRight}
            />
          </>
        )}
      </section>

      <div className={kittingPickNumpadSplitGridClass(numpadOnRight)}>
        <div
          className={cn(
            "flex min-h-0 flex-col gap-2 overflow-hidden",
            kioskMainPaneOrderClass(numpadOnRight),
          )}
        >
          <section
            aria-label="Cart bin contents"
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white p-2"
          >
            <h2 className="shrink-0 border-b border-slate-100 pb-1.5 text-sm font-semibold text-slate-900">
              Cart bin
            </h2>
            <div className="flex h-0 min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]">
              <KittingPickCartPanel
                entries={cartEntries}
                marksBySlotId={cartMarks}
                hoverHints={hoverHints}
                disabled={!canWrite || workspaceDisabled}
                returningEntryId={returningCartEntryId}
                onReturnEntry={canWrite ? handleReturnCartEntry : undefined}
                onReturnContainer={canWrite ? handleReturnCartContainer : undefined}
              />
            </div>
          </section>
        </div>

        <section
          aria-label="Pick quantity"
          className={cn(
            "flex min-h-0 flex-col gap-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2",
            kioskNumpadPaneOrderClass(numpadOnRight),
            numpadOnBottom ? "justify-end" : "justify-start",
          )}
        >
          {numpadOnBottom ? (
            <div className={cn("shrink-0", numpadOnRight ? "self-end" : "self-start")}>
              <KittingPickNumpadCornerToggle corner={numpadCorner} onCornerChange={setNumpadCorner} />
            </div>
          ) : null}
          {workspaceDisabled ? (
            <p className="text-sm text-slate-600">
              {!canWrite
                ? "Manufacturing write access is required."
                : scopedPickLines.length === 0
                  ? "No open pick lines for the selected jobs."
                  : null}
            </p>
          ) : (
            <>
              <TransferPackQuantityReadout
                kiosk
                selectedSku={selectedLine?.sku ?? selectedSku}
                quantityDraft={quantityDraft}
                emptySelectionLabel="Tap line or scan"
                onClearSelection={inputMode === "quantity" ? returnToScanMode : undefined}
              />
              <TransferPackNumpad
                disabled={inputMode !== "quantity" || selectedLine == null || numpadLocked}
                onDigit={appendDigit}
                onClear={clearQuantity}
                onBackspace={backspaceQuantity}
                onEnter={submitQuantity}
              />
            </>
          )}
          {!numpadOnBottom ? (
            <div className={cn("shrink-0", numpadOnRight ? "self-end" : "self-start")}>
              <KittingPickNumpadCornerToggle corner={numpadCorner} onCornerChange={setNumpadCorner} />
            </div>
          ) : null}
        </section>
      </div>
      <KittingInventoryBoxPickModal
        box={scannedInventoryBox}
        printer={selectedPrinter}
        station={station}
        documents={documents}
        onStationChange={setStation}
        unavailableMessage={unavailableMessage}
        onCancel={() => {
          setScannedInventoryBox(null);
          clearScannedContainer();
          focusScanInput(scanInputRef.current);
        }}
        onPickQuantity={(sku, quantity, inventoryBoxHexes, unpickedInventoryBoxHexes) => {
          setScannedInventoryBox(null);
          selectedInventoryBoxHexesRef.current = inventoryBoxHexes;
          unpickedInventoryBoxHexesRef.current = unpickedInventoryBoxHexes ?? [];
          const skuMatchesSelection =
            selectedLine != null
            && selectedLine.sku.trim().toLowerCase() === sku.trim().toLowerCase();
          const onlyLine = kittingPickLinesMatchingSku(scopedPickLines, sku);
          const line = skuMatchesSelection && selectedLine != null
            ? selectedLine
            : onlyLine.length === 1
              ? onlyLine[0]!
              : null;
          if (line != null) {
            setQuantityDraft("");
            commitContainerPickRef.current(line, quantity);
            return;
          }
          scannedContainerQuantityRef.current = quantity;
          selectSku(sku, true, true);
        }}
      />
      <WarehouseRecountModal
        open={recountOpen}
        initialSku={selectedSku}
        onClose={() => {
          setRecountOpen(false);
          focusScanInput(scanInputRef.current);
        }}
      />
    </div>
  );
}

function KittingPickSlotChip({
  slot,
  mark,
}: {
  slot: KittingCartSlot;
  mark: KittingCartMark | null;
}): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-medium text-slate-900",
        mark != null ? mark.chipClass : "border-slate-200 bg-slate-50",
      )}
    >
      {mark != null ? (
        <span
          className={cn(
            "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
            mark.badgeClass,
          )}
          aria-hidden
        >
          {mark.letter}
        </span>
      ) : null}
      <span className="truncate">{slot.headlineLabel}</span>
    </span>
  );
}
